import { useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/common/toast-context';
import { useErrorMessage } from '@/features/auth/useAuthErrorMessage';
import type { ReportReason } from '@/lib/schemas/discovery';

import { useReport, type ReportTarget } from './useDiscovery';

/**
 * The reasons a reader can pick from (spec §8.6).
 *
 * Each one is phrased as what happened rather than as a policy name — a
 * reporter knows "someone posted my address", not "PERSONAL_INFO".
 */
const REASONS: ReportReason[] = [
  'SPAM',
  'HARASSMENT',
  'HATE',
  'MISINFORMATION',
  'PERSONAL_INFO',
  'OTHER',
];

const DETAILS_MAX = 1000;

interface ReportDialogProps {
  target: ReportTarget | null;
  onClose: () => void;
}

export function ReportDialog({ target, onClose }: ReportDialogProps) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const [reason, setReason] = useState<ReportReason>('SPAM');
  const [details, setDetails] = useState('');
  const report = useReport();
  const { showToast } = useToast();

  const close = (): void => {
    onClose();
    // Reset after the close transition so the form does not visibly blank out
    // while the dialog is still fading.
    window.setTimeout(() => {
      setReason('SPAM');
      setDetails('');
      report.reset();
    }, 200);
  };

  const submit = (): void => {
    if (!target) return;

    report.mutate(
      { target, reason, ...(details.trim() ? { details: details.trim() } : {}) },
      {
        onSuccess: (result) => {
          showToast(result.message, 'success');
          close();
        },
      },
    );
  };

  return (
    <Dialog
      open={target !== null}
      onClose={close}
      aria-labelledby="report-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="report-title">
        {t(`safety.reportTitle.${target?.kind ?? 'POST'}`)}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {report.isError && (
            <Alert severity="error" role="alert">
              {errorMessage(report.error, t('safety.reportFailed'))}
            </Alert>
          )}

          {target && (
            <Typography variant="body2" color="text.secondary" noWrap>
              “{target.label}”
            </Typography>
          )}

          <RadioGroup
            aria-label={t('safety.whyReporting')}
            value={reason}
            onChange={(event) => setReason(event.target.value as ReportReason)}
          >
            {REASONS.map((reason) => (
              <FormControlLabel
                key={reason}
                value={reason}
                control={<Radio size="small" />}
                sx={{ alignItems: 'flex-start', mb: 0.5 }}
                label={
                  <Stack spacing={-0.25} sx={{ pt: 0.75 }}>
                    <Typography variant="body2">{t(`safety.reasons.${reason}.label`)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`safety.reasons.${reason}.hint`)}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </RadioGroup>

          <TextField
            label={t('safety.details')}
            value={details}
            onChange={(event) => setDetails(event.target.value.slice(0, DETAILS_MAX))}
            multiline
            minRows={2}
            size="small"
            fullWidth
            helperText={`${details.length}/${DETAILS_MAX}`}
          />

          <Typography variant="caption" color="text.secondary">
            {t('safety.reportPrivacy')}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" onClick={close}>
          {t('common.cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={report.isPending}>
          {report.isPending ? t('common.sending') : t('safety.sendReport')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
